import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useReducer } from 'react';
import withLogger from '../util/withLogger';

/**
 * The current state of the toast store.
 *
 * @typedef {Object} ToastState
 *
 * @property {Map} toasts Map object associating an id to toast data
 */
var initialState = {
  toasts: new Map()
};
var reducer = function () {
  var prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var {
    type,
    payload
  } = action;
  switch (type) {
    case 'add':
      {
        var nextToasts = new Map(prevState.toasts);
        var prevToast = nextToasts.get(payload.id);
        var isDuplicate = !!prevToast;
        var timestamp = payload.timestamp;
        if (isDuplicate) {
          // If this is a _new_ duplicate toast we need to clear the
          // previous timeout to prevent premature removal.
          window.clearTimeout(prevToast.removalTimeoutId);

          // And to retain chronological order of addition, keep the
          // original timestamp.
          timestamp = prevToast.timestamp;
        }
        nextToasts.set(payload.id, _objectSpread(_objectSpread({}, payload), {}, {
          timestamp,
          isDuplicate
        }));
        return _objectSpread(_objectSpread({}, prevState), {}, {
          toasts: nextToasts
        });
      }
    case 'remove':
      {
        var _nextToasts = new Map(prevState.toasts);
        var _prevToast = _nextToasts.get(payload.id);
        if (_prevToast) {
          window.clearTimeout(_prevToast.removalTimeoutId);
        }
        _nextToasts.delete(payload.id);
        return _objectSpread(_objectSpread({}, prevState), {}, {
          toasts: _nextToasts
        });
      }
    default:
      return prevState;
  }
};
var ToastContext = createContext();
var wrappedReducer = withLogger(reducer);

/**
 * A [context]{@link https://reactjs.org/docs/context.html} provider that
 * provides the toast state object and a dispatch function to toast
 * functionality consumers.
 *
 * @typedef ToastContextProvider
 *
 */
export var ToastContextProvider = _ref => {
  var {
    children
  } = _ref;
  var store = useReducer(wrappedReducer, initialState);
  return /*#__PURE__*/React.createElement(ToastContext.Provider, {
    value: store
  }, children);
};

/**
 * A hook that provides access to the toast state and dispatch.
 * Any component using this hook _must_ be a child of a {@link ToastContextProvider}.
 *
 * @typedef useToastContext
 *
 * @return {Object[]} An array containing the state and dispatch function: [{@link ToastState}, function]
 *
 * @example
 *   const [toastState, dispatch] = useToastState();
 */
export var useToastContext = () => useContext(ToastContext);